import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Toast } from "primereact/toast";
import AppLoading from '../app-loading/app-loading';
import ComplaintSummaryDataTable from './ComplaintSummaryDataTable';
import ComplaintSummarySearch from './ComplaintSummarySearch';
import RegisterComplaintDialog from './RegisterComplaintDialog';
import { getComplaintSummary, ComplaintSummaryRequest, ComplaintSummaryItem, getComplaintAlertCounts } from '../../services/order/order.service';
import { getComplaintQuickView } from '../../services/order/order.service';
import orderServices from '../../services/orders/orderservice';
import { color } from 'd3';
import * as XLSX from 'xlsx';

const ComplaintSummaryMain: React.FC = () => {
    const toast = useRef<Toast>(null);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [complaints, setComplaints] = useState<ComplaintSummaryItem[]>([]);
    const [pageNo, setPageNo] = useState(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [showRegisterDialog, setShowRegisterDialog] = useState(false);
    const [activeCardFilter, setActiveCardFilter] = useState<'highInvoice' | 'oneWeekDelay' | 'newComplaints' | 'filterByValueAtRisk' | 'inProgress' | null>(null);
    const [highInvoiceCount, setHighInvoiceCount] = useState<number>(0);
    const [oneWeekDelayCount, setOneWeekDelayCount] = useState<number>(0);
    const [newComplaintsCount, setNewComplaintsCount] = useState<number>(0);
    const [inProgressCount, setInProgressCount] = useState<number>(0);
    const [valueAtRiskCount, setValueAtRiskCount] = useState<number>(0);
    const [totalCostPerQtyInRs, setTotalCostPerQtyInRs] = useState<number>(0);
    const [complaintCostInPercentage, setComplaintCostInPercentage] = useState<number>(0);

    const [filters, setFilters] = useState<Partial<ComplaintSummaryRequest>>({
        complaintTypeId: null,
        statusId: null,
        priority: null,
        customerId: null,
        createdBy: null,
        createdOnFrom: null,
        createdOnTo: null,
        sortBy: 'critical',
        sortOrder: null,
        filterByHighInvoice: null,
        filterByOneWeekDelay: null,
        filterByValueAtRisk: null
    });

    const fetchAlertCounts = async () => {
        try {
            const counts = await getComplaintAlertCounts();
            if (counts) {
                setHighInvoiceCount(counts.highInvoiceCount);
                setOneWeekDelayCount(counts.oneWeekDelayCount);
                setNewComplaintsCount(counts.newComplaintsCount);
                setInProgressCount(counts.inProgressCount);
                setValueAtRiskCount(counts.valueAtRiskCount);
            }
        } catch (error) {
            console.error("Failed to fetch alert counts", error);
        }
    };

    const loadComplaints = async (page: number = pageNo, size: number = pageSize, searchFilters: Partial<ComplaintSummaryRequest> = filters) => {
        setLoading(true);
        try {
            const request: ComplaintSummaryRequest = {
                pageNumber: page,
                pageSize: size,
                complaintTypeId: searchFilters.complaintTypeId || null,
                statusId: searchFilters.statusId || null,
                priority: searchFilters.priority || null,
                customerId: searchFilters.customerId || null,
                createdBy: searchFilters.createdBy || null,
                createdOnFrom: searchFilters.createdOnFrom || null,
                createdOnTo: searchFilters.createdOnTo || null,
                sortBy: searchFilters.sortBy || 'critical',
                sortOrder: searchFilters.sortOrder || null,
                filterByHighInvoice: searchFilters.filterByHighInvoice || null,
                filterByOneWeekDelay: searchFilters.filterByOneWeekDelay || null,
                filterByValueAtRisk: searchFilters.filterByValueAtRisk || null
            };

            const response = await getComplaintSummary(request);

            setComplaints(response.complaints || []);
            setTotalRecords(response.totalRecords || 0);
            setTotalPages(response.totalPages || 0);
            setPageNo(response.pageNumber || page);
            setPageSize(response.pageSize || size);
            setTotalCostPerQtyInRs(response.totalCostPerQtyInRs || 0);
            setComplaintCostInPercentage(response.complaintCostInPercentage || 0);

            // Fetch latest alert counts from the dedicated API
            fetchAlertCounts();

        } catch (error) {
            console.error('Error loading complaint summary:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load complaint summary',
                life: 3000
            });
            setComplaints([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setLoading(true);
        try {
            const request: ComplaintSummaryRequest = {
                pageNumber: 1,
                pageSize: 100000,
                complaintTypeId: filters.complaintTypeId || null,
                statusId: filters.statusId || null,
                priority: filters.priority || null,
                customerId: filters.customerId || null,
                createdBy: filters.createdBy || null,
                createdOnFrom: filters.createdOnFrom || null,
                createdOnTo: filters.createdOnTo || null,
                sortBy: filters.sortBy || 'critical',
                sortOrder: filters.sortOrder || null,
                filterByHighInvoice: filters.filterByHighInvoice || null,
                filterByOneWeekDelay: filters.filterByOneWeekDelay || null,
                filterByValueAtRisk: filters.filterByValueAtRisk || null
            };

            const response = await getComplaintSummary(request);
            const complaintsForExport = response.complaints || [];
            const quickViews = await Promise.all(complaintsForExport.map(async (complaint) => {
                try {
                    return await getComplaintQuickView(complaint.complaintId);
                } catch {
                    return null;
                }
            }));

            const userIds = new Set<number>();
            quickViews.forEach((quickView) => {
                if (quickView?.createdBy) userIds.add(Number(quickView.createdBy));
                if (quickView?.modifiedBy) userIds.add(Number(quickView.modifiedBy));
            });

            const phoneByUserId = new Map<number, string>();
            await Promise.all(Array.from(userIds).map(async (userId) => {
                try {
                    const userResponse = await orderServices.getUserDetailComplaint(userId);
                    const user = Array.isArray(userResponse)
                        ? userResponse[0]
                        : userResponse?.data || userResponse?.result || userResponse?.user || userResponse;
                    const phone = String(
                        user?.primaryContactNbr ??
                        user?.primaryContactNumber ??
                        user?.primaryContact ??
                        user?.contactNumber ??
                        user?.phoneNumber ??
                        user?.mobileNumber ??
                        user?.mobile ??
                        user?.phone ??
                        ''
                    ).trim();
                    if (phone) phoneByUserId.set(userId, phone);
                } catch (error) {
                    console.warn(`Unable to load phone number for user ${userId}`, error);
                }
            }));

            const rows = complaintsForExport.map((complaint, index) => {
                const quickView = quickViews[index];
                const customerDetails = quickView?.productDetail || quickView?.serviceDetail;
                return {
                    'ComplaintID': complaint.complaintId,
                    'CustomerId': customerDetails?.customerId || '',
                    'CustomerName': complaint.customer || customerDetails?.customerName || '',
                    'CustomerPhoneNumber': complaint.phonenumber || '',
                    'ComplaintType': complaint.complaintType || quickView?.complaintTypeName || '',
                    'ComplaintDescription': quickView?.description || '',
                    'ComplaintActions': (quickView?.actions || [])
                        .map(action => action.actionDescription)
                        .filter(Boolean)
                        .join('; '),
                    'CorrectiveAction': quickView?.correctiveAction || '',
                    'PreventiveAction': quickView?.preventiveAction || '',
                    'InvoiceValue': complaint.invoiceValue ?? '',
                    'Status': complaint.status || quickView?.statusName || '',
                    'ComplaintCreatedDate': complaint.createdOn || quickView?.createdDate || '',
                    'CreatedBy': quickView?.createdByName || complaint.createdBy || '',
                    'CreatedByPhone#': phoneByUserId.get(Number(quickView?.createdBy)) || '',
                    'ModifiedBy': quickView?.modifiedByName || complaint.lastModifiedBy || '',
                    'ModifiedByPhone#': phoneByUserId.get(Number(quickView?.modifiedBy)) || ''
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            worksheet['!cols'] = [
                { wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 20 },
                { wch: 18 }, { wch: 50 }, { wch: 50 }, { wch: 30 },
                { wch: 30 }, { wch: 16 }, { wch: 16 }, { wch: 24 },
                { wch: 24 }, { wch: 20 }, { wch: 24 }, { wch: 20 }
            ];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Complaints');
            XLSX.writeFile(workbook, 'ComplaintSummary.xlsx');
        } catch (error) {
            console.error('Error exporting complaints:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Export Failed',
                detail: 'Unable to export complaints.',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadComplaints();
    }, []);

    const handleSearch = (searchFilters: Partial<ComplaintSummaryRequest>) => {
        // Clear card filter if this is a reset (all filters are null)
        const isReset = !searchFilters.complaintTypeId &&
            !searchFilters.statusId &&
            !searchFilters.priority &&
            !searchFilters.customerId &&
            !searchFilters.createdOnFrom &&
            !searchFilters.createdOnTo;

        if (isReset) {
            setActiveCardFilter(null);
        }

        const updatedFilters = { ...filters, ...searchFilters };
        setFilters(updatedFilters);
        setPageNo(1);
        loadComplaints(1, pageSize, updatedFilters);
    };

    const handlePageChange = async (event: any) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setPageNo(newPageNumber);
        setPageSize(event.rows);
        loadComplaints(newPageNumber, event.rows, filters);
    };

    const handleSort = (event: any) => {
        const { sortField, sortOrder } = event;
        const newFilters = {
            ...filters,
            sortBy: sortField,
            sortOrder: sortOrder === 1 ? 'asc' : 'desc'
        };
        handleSearch(newFilters);
    };

    const handleDropdownSort = (value: string) => {
        const newFilters = {
            ...filters,
            sortBy: value,
            sortOrder: null
        };
        handleSearch(newFilters);
    };

    const handleCardClick = (cardType: 'highInvoice' | 'oneWeekDelay' | 'newComplaints' | 'filterByValueAtRisk' | 'inProgress') => {
        // Toggle the filter if clicking the same card, otherwise set new filter
        const newActiveFilter = activeCardFilter === cardType ? null : cardType;
        setActiveCardFilter(newActiveFilter);

        const newFilters = {
            ...filters,
            filterByHighInvoice: newActiveFilter === 'highInvoice' ? true : null,
            filterByOneWeekDelay: newActiveFilter === 'oneWeekDelay' ? true : null,
            statusId: newActiveFilter === 'newComplaints' ? 1 : (newActiveFilter === 'inProgress' ? 2 : null),
            filterByValueAtRisk: newActiveFilter === 'filterByValueAtRisk' ? true : null
        };

        setFilters(newFilters);
        setPageNo(1);
        loadComplaints(1, pageSize, newFilters);
    };

    return (
        <>
            <Suspense fallback={<AppLoading />}>
                <Toast ref={toast} position="top-center" />
                <style>{`
                    .complaint-header-card {
                        background: linear-gradient(135deg, #f8fffe 0%, #e8f5e9 100%);
                        border-radius: 16px;
                        padding: 1.5rem 2rem;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                        border: 1px solid rgba(76, 175, 80, 0.1);
                        margin-bottom: 1.5rem;
                    }

                    .complaint-title-wrapper {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                    }

                    .complaint-icon-wrapper {
                        width: 48px;
                        height: 48px;
                        background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
                    }

                    .complaint-icon-wrapper i {
                        color: white;
                        font-size: 1.5rem;
                    }

                    .register-complaint-btn {
                        background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
                        border: none !important;
                        border-radius: 10px !important;
                        padding: 0.65rem 1.5rem !important;
                        font-weight: 600 !important;
                        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
                        transition: all 0.3s ease !important;
                    }

                    .register-complaint-btn:hover {
                        transform: translateY(-2px) !important;
                        box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
                    }

                    .register-complaint-btn:active {
                        transform: translateY(0) !important;
                    }

                    .stats-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.5rem;
                        background: white;
                        padding: 0.4rem 0.9rem;
                        border-radius: 20px;
                        font-size: 0.85rem;
                        font-weight: 600;
                        color: #4CAF50;
                        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
                        margin-top: 0.5rem;
                    }

                    .stats-badge i {
                        font-size: 0.9rem;
                    }

                    .stats-cards-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                        gap: 1rem;
                        margin-bottom: 2rem;
                    }

                    @media screen and (max-width: 768px) {
                        .complaint-header-card .flex {
                            flex-direction: column;
                            align-items: flex-start !important;
                            gap: 1rem;
                        }
                        
                        .register-complaint-btn {
                            width: 100%;
                            justify-content: center;
                        }

                        .stats-cards-grid {
                            grid-template-columns: 1fr 1fr; 
                        }
                    }

                    @media screen and (max-width: 480px) {
                        .stats-cards-grid {
                            grid-template-columns: 1fr;
                        }
                    }

                    .mini-stat-card {
                        background: white;
                        border-radius: 12px;
                        padding: 1rem 1.5rem;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                        border: 1px solid rgba(0, 0, 0, 0.05);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        transition: transform 0.2s;
                        height: 90px;
                    }

                    .mini-stat-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    }

                    .mini-stat-card.clickable {
                        cursor: pointer;
                    }

                    .mini-stat-card.active {
                        border: 2px solid #4CAF50;
                        box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
                    }

                    .stat-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 1.2rem;
                    }

                    .stat-content h3 {
                        margin: 0;
                        font-size: 1.5rem;
                        font-weight: 700;
                        color: #10492a;
                    }

                    .stat-content p {
                        margin: 0;
                        font-size: 0.85rem;
                        color: #6c757d;
                        margin-top: 0.25rem;
                        font-weight: 500;
                    }
                `}</style>

                <div className="layout-content mt-3 mb-3">
                    {/* Enhanced Header */}
                    <div className="complaint-header-card">
                        <div className="flex align-items-center justify-content-between">
                            <div>
                                <div className="complaint-title-wrapper">
                                    <div className="complaint-icon-wrapper">
                                        <i className="pi pi-megaphone"></i>
                                    </div>
                                    <div>
                                        <h1 className="text-800 font-bold text-3xl m-0" style={{ color: "#10492a" }}>
                                            Manage Complaints
                                        </h1>
                                        <p className="text-600 m-0 text-sm mt-1">
                                            Track, manage, and resolve customer complaints efficiently
                                        </p>
                                    </div>
                                </div>
                                {/* {totalRecords > 0 && (
                                    <div className="stats-badge">
                                        <i className="pi pi-chart-bar"></i>
                                        <span> Complaints</span>
                                    </div>
                                )} */}
                            </div>
                            <Button
                                label="Register Complaint"
                                icon="pi pi-plus"
                                onClick={() => setShowRegisterDialog(true)}
                                className="register-complaint-btn"
                            />
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="stats-cards-grid">
                        <div
                            className={`mini-stat-card clickable ${activeCardFilter === 'highInvoice' ? 'active' : ''}`}
                            onClick={() => handleCardClick('highInvoice')}
                            title="Click to filter complaints with high invoice value (>₹100)"
                        >
                            <div className="stat-content">
                                <h3>{highInvoiceCount}</h3>
                                <p>Product Complaint Value {'>'} ₹100</p>
                            </div>
                            <div className="stat-icon" style={{ background: '#e0f7fa', color: '#00838f' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>₹</span>
                            </div>
                        </div>
                        <div
                            className={`mini-stat-card clickable ${activeCardFilter === 'oneWeekDelay' ? 'active' : ''}`}
                            onClick={() => handleCardClick('oneWeekDelay')}
                            title="Click to filter complaints pending for more than one week"
                        >
                            <div className="stat-content">
                                <h3>{oneWeekDelayCount}</h3>
                                <p>Older Than 7 Days</p>
                            </div>
                            <div className="stat-icon" style={{ background: '#ffebee', color: '#c62828' }}>
                                <i className="pi pi-exclamation-triangle"></i>
                            </div>
                        </div>
                        <div
                            className={`mini-stat-card clickable ${activeCardFilter === 'newComplaints' ? 'active' : ''}`}
                            onClick={() => handleCardClick('newComplaints')}
                            title="Click to filter New complaints"
                        >
                            <div className="stat-content">
                                <h3>{newComplaintsCount}</h3>
                                <p>New Complaints</p>
                            </div>
                            <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                                <i className="pi pi-file"></i>
                            </div>
                        </div>
                        <div
                            className={`mini-stat-card clickable ${activeCardFilter === 'inProgress' ? 'active' : ''}`}
                            onClick={() => handleCardClick('inProgress')}
                            title="Click to filter In Progress complaints"
                        >
                            <div className="stat-content">
                                <h3>{inProgressCount}</h3>
                                <p>In Progress</p>
                            </div>
                            <div className="stat-icon" style={{ background: '#fff3e0', color: '#ef6c00' }}>
                                <i className="pi pi-sync"></i>
                            </div>
                        </div>
                        <div
                            className={`mini-stat-card clickable ${activeCardFilter === 'filterByValueAtRisk' ? 'active' : ''}`}
                            onClick={() => handleCardClick('filterByValueAtRisk')}
                            title="Click to filter In Progress complaints"
                        >
                            <div className="stat-content">
                                <h3>
                                    ₹ {Number(valueAtRiskCount).toLocaleString('en-IN', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}
                                </h3>
                                <p>Product Complaint Value At Risk</p>
                            </div>
                            <div className="stat-icon" style={{ background: '#e0f7fa', color: '#00838f' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>₹</span>
                            </div>
                        </div>
                    </div>

                    {/* Search Panel */}
                    <ComplaintSummarySearch
                        onSearch={handleSearch}
                        setLoading={setLoading}
                        onExport={handleExport}
                    />

                    {/* Data Table */}
                    <div className="mt-4">
                        <ComplaintSummaryDataTable
                            data={complaints}
                            pageNo={pageNo}
                            pageSize={pageSize}
                            handlepagechange={handlePageChange}
                            totalRecordsCount={totalRecords}
                            onRefresh={() => loadComplaints()}
                            sortField={filters.sortBy || undefined}
                            sortOrder={filters.sortOrder === 'desc' ? -1 : 1}
                            onSort={handleSort}
                            onSortChange={handleDropdownSort}
                            totalCost={totalCostPerQtyInRs}
                            costPercentage={complaintCostInPercentage}
                        />
                    </div>
                </div>
                {loading && <AppLoading />}

                {/* Register Complaint Dialog */}
                <RegisterComplaintDialog
                    visible={showRegisterDialog}
                    onHide={() => setShowRegisterDialog(false)}
                    onSuccess={() => {
                        loadComplaints();
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Complaint registered successfully',
                            life: 3000
                        });
                    }}
                />
            </Suspense>
        </>
    );
};

export default ComplaintSummaryMain;
